/**
 * useBookingNotifications — realtime toast + cache invalidation
 * when a client books via /book/:slug (#357).
 *
 * Mounts once at the app-shell level and subscribes to inserts on
 * `bookings` scoped to the current studio's org. On new booking:
 *
 *   • Fires a toast so the studio sees the booking land wherever
 *     they are in the app (not just Calendar)
 *   • Invalidates the bookings + calendar-events queries so the
 *     surface auto-refreshes without a manual reload
 *
 * iOS push is fanned out from the send-booking-confirmation edge
 * function separately — this hook is web-only counterpart parity
 * with what iOS gets via APNs.
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrgId } from '@/hooks/useOrgId';
import { toast } from 'sonner';
import { format } from 'date-fns';

export function useBookingNotifications() {
  const qc = useQueryClient();
  const { data: orgId } = useOrgId();

  useEffect(() => {
    if (!orgId) return;

    // Filter server-side so a studio only receives events for their
    // own org. Postgres RLS also covers this but explicit filter
    // avoids unnecessary websocket traffic.
    const channel = supabase
      .channel(`bookings:${orgId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'bookings',
          filter: `org_id=eq.${orgId}`,
        },
        (payload) => {
          const b: any = payload.new;
          if (!b) return;
          const when = b.start_at ? format(new Date(b.start_at), 'MMM d · h:mm a') : '';
          toast.success(`New booking — ${b.client_name}`, {
            description: when,
            duration: 8000,
            action: {
              label: 'View',
              onClick: () => {
                // Deep-link to the calendar page. The booking will
                // be rendered as a green event; the studio can tap
                // it for details from there.
                window.location.assign('/calendar');
              },
            },
          });
          // Refresh the queries that show this data so any
          // currently-open surface picks it up without a reload.
          qc.invalidateQueries({ queryKey: ['bookings'] });
          qc.invalidateQueries({ queryKey: ['calendar-events'] });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [orgId, qc]);
}
